import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { MultiSelectProps } from "./TMultiSelect";

import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { Box, MenuItem } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getStyles = (
  name: string,
  selectedNames: readonly string[],
  theme: Theme,
) => ({
  fontWeight: selectedNames.includes(name)
    ? theme.typography.fontWeightMedium
    : theme.typography.fontWeightRegular,
});

const MUIMultiSelect: React.FC<MultiSelectProps> = ({
  name,
  control,
  label,
  options,
  rules,
  required = false,
  error,
  helperText,
  tabIndex = 0,
  autoFocus = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const memoizedOptions = useMemo(() => options, [options]);

  return (
    <FormControl fullWidth required={required} error={error} disabled={disabled}>
      <InputLabel
        id={`${name}-chip-label`}
        sx={{
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
      >
        {label}
      </InputLabel>
      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue={[]}
        render={({ field }) => (
          <>
            <Select
              labelId={`${name}-chip-label`}
              aria-label={`${label} ${field.value}`}
              id={`${name}-chip`}
              multiple
              autoFocus={autoFocus}
              value={Array.isArray(field.value) ? field.value : []}
              onChange={(event) => field.onChange(event.target.value)}
              input={
                <OutlinedInput
                  id={`select-multiple-${name}`}
                  label={label}
                  inputProps={{
                    tabIndex,
                  }}
                />
              }
              sx={{
                "& .MuiSelect-select": {
                  paddingBottom: field.value?.length > 0 ? "9px" : "16.5px",
                  paddingTop: field.value?.length > 0 ? "15px" : "16.5px",
                },
              }}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    width: "fit-content",
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {selected.map((value: string) => (
                    <Chip
                      key={value}
                      label={value}
                      tabIndex={tabIndex}
                      onDelete={() => {
                        field.onChange(
                          field.value.filter((chip: string) => chip !== value),
                        );
                      }}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {memoizedOptions.map((option, index) => (
                <MenuItem
                  key={`${option.value}-${index + 1}`}
                  value={option.value}
                  style={getStyles(option.label, field.value, theme)}
                  tabIndex={tabIndex}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </>
        )}
      />
    </FormControl>
  );
};

export default MUIMultiSelect;
